import { logger } from '@accedo/logger';
import { DialogManager } from '@accedo/ctv-ui-dialog';
import { setLoadingIndicatorVisibility, setLoadingSolidBackground } from '##/components/LoadingIndicator';
import { config } from '##/services/store';
import { Route as ElevateRoute } from '##/dataModels/route';
import { ExitDialogElement } from '##/components/Dialogs/ExitDialog';

let prevPathname = window.location.pathname;
let expectedTemplate: string | undefined;
let dynamicLoadStarted = false;
let isBackNavigation = false;

export const getAppBaseUrl = (): string => process.env.PUBLIC_PATH || '/';
const LP = '[Routing utils]';

export const addPublicPath = (path: string): string => {
  const publicPath = getAppBaseUrl();
  if (publicPath.length > 1 && path.startsWith(publicPath)) {
    return path;
  }
  return `${publicPath}${path.startsWith('/') ? path.substring(1) : path}`;
};

export const removePublicPath = (path: string): string => {
  const publicPath = getAppBaseUrl();
  return publicPath === '/' ? path : path.replace(publicPath, '/');
};

const routeChangeInternal = (path: string): void => {
  const elevateRoutes: ElevateRoute[] = config.getByKey<ElevateRoute[]>('routes');

  // Routes might not be loaded yet (before authentication)
  if (!elevateRoutes || elevateRoutes.length === 0) {
    logger.debug(`${LP} Routes not loaded yet, skipping route change`);
    return;
  }

  const cleanPath = removePublicPath(path);
  const route = elevateRoutes.find((er) => cleanPath === er.route || cleanPath.startsWith(`${er.route}/`));
  expectedTemplate = route?.page.template;

  // Override template for search page if backend sends wrong template
  if (cleanPath === '/search' && expectedTemplate !== 'elevate-search') {
    logger.debug(`${LP} Overriding template for /search route from "${expectedTemplate}" to "elevate-search"`);
    expectedTemplate = 'elevate-search';
  }

  if (path !== prevPathname) {
    prevPathname = path;
  }

  // Clear accumulated browser performance timeline entries on every route change.
  // In a SPA, Chrome never evicts these automatically — after a playback session
  // 80 000+ PerformanceMeasure/ResourceTiming entries accumulate (~10-11 MB heap).
  // This has no effect on the browser HTTP cache or any in-flight requests.
  performance.clearResourceTimings();
  performance.clearMeasures();
  performance.clearMarks();

  // pages need to turn off the loading indicator
  setLoadingSolidBackground(false);
  setLoadingIndicatorVisibility(true);
};

// export const onBackNavigation = (event: PopStateEvent): void => {
//   logger.debug(`${LP} onBackNavigation `, event);
//   isBackNavigation = true;
// };

// https://github.com/preactjs/preact-iso/blob/main/README.md

/** Route is loaded. */
export const onRouteChange = (url: string): void => {
  logger.debug(`${LP} route changed to ${url} previous isBackNavigation previous state: ${isBackNavigation}`);
  isBackNavigation = false;

  if (!dynamicLoadStarted) {
    routeChangeInternal(url);
  }
};

/**
 * Start of loading a dynamic route the first time.
 * This will not be called before navigations to sync routes
 * or subsequent navigations to async routes.
 */
export const onLoadStart = (url: string): void => {
  logger.debug(`${LP} onLoadStart url ${url}`);
  dynamicLoadStarted = true;
  routeChangeInternal(url);
};

/**
 * End of loading a dynamic route the first time.
 * This will not be called after navigations to sync routes
 * or subsequent navigations to async routes.
 */
export const onLoadEnd = (url: string): void => {
  logger.debug(`${LP} onLoadEnd url ${url}`);
  dynamicLoadStarted = false;
};

export const getExpectedTemplate = (): string | undefined => expectedTemplate;

export const getHomePath = (): string => {
  let defaultRoute = config.getByKey<string>('defaultRoute');
  if (!defaultRoute.endsWith('/')) {
    defaultRoute += '/';
  }
  return addPublicPath(defaultRoute);
};

export const isOnHomePath = (): boolean => {
  let defaultRoute = config.getByKey<string>('defaultRoute');
  if (!defaultRoute.endsWith('/')) {
    defaultRoute += '/';
  }
  return window.location.pathname === addPublicPath(defaultRoute);
};

export const getIsBackNavigation = (): boolean => isBackNavigation;

export const goBack = (): void => {
  logger.debug(
    `${LP} goBack isBackNavigation  prevPathname: ${prevPathname} window.location.pathname: ${window.location.pathname}`,
  );

  // If a dialog is open, dismiss it instead of navigating away.
  if (DialogManager.isDialogOpen()) {
    logger.debug(`${LP} goBack: dialog is open, closing instead of navigating`);
    DialogManager.close();
    return;
  }

  const initialPaths = [getHomePath(), addPublicPath('/landing')];

  if (initialPaths.includes(window.location.pathname)) {
    logger.debug(`${LP} goBack calling open exit dialog`);
    DialogManager.open(ExitDialogElement);
  } else {
    isBackNavigation = true;
    window.history.back();
  }
};

/**
 * Global back key handling.
 */
export const handleGlobalBackKey = (): boolean => {
  goBack();
  return true;
};
